import React from 'react';
import {
  Box,
  Autocomplete,
  TextField,
  InputAdornment,
  Button,
  IconButton,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';

/**
 * ProductionMetricsFilterBar
 * A modern, premium client-side filter bar that allows filtering packages by:
 * 1. Area (Dropdown of project leaf areas)
 * 2. PM (Dropdown of active PMs on the project)
 * 3. Search (Keystroke contains match on name + description)
 */
const ProductionMetricsFilterBar = ({
  projectId,
  areas = [],
  pmOptions = [],
  filterAreaName,
  setFilterAreaName,
  filterPmId,
  setFilterPmId,
  filterSearchText,
  setFilterSearchText,
  onClearFilters,
}) => {
  // If no project is selected, do not show the filter controls
  if (!projectId) return null;

  const isAnyFilterActive =
    Boolean(filterAreaName) ||
    Boolean(filterPmId) ||
    Boolean(filterSearchText.trim());

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 1.5,
      }}
    >
      {/* Vertical Separator */}
      <Box
        sx={{
          borderLeft: '1px solid #e2e8f0',
          height: 28,
          mx: { xs: 0.5, sm: 1 },
          display: { xs: 'none', md: 'block' },
        }}
      />

      {/* Area Filter Dropdown */}
      <Autocomplete
        size="small"
        options={areas}
        getOptionLabel={(option) => option?.name || option || ''}
        value={areas.find((a) => a.name === filterAreaName) || null}
        onChange={(_, newValue) => {
          setFilterAreaName(newValue ? newValue.name : null);
        }}
        isOptionEqualToValue={(option, value) => option?.name === value?.name}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Area"
            placeholder="Select Area"
            sx={{
              width: 190,
              '& .MuiInputBase-root': {
                fontSize: '13px',
                bgcolor: '#f8fafc',
                borderRadius: '6px',
              },
              '& .MuiInputLabel-root': {
                fontSize: '13px',
              },
            }}
          />
        )}
      />

      {/* PM Filter Dropdown */}
      <Autocomplete
        size="small"
        options={pmOptions}
        getOptionLabel={(option) => option?.name || ''}
        value={pmOptions.find((p) => p.id === filterPmId) || null}
        onChange={(_, newValue) => {
          setFilterPmId(newValue ? newValue.id : null);
        }}
        isOptionEqualToValue={(option, value) => option?.id === value?.id}
        renderInput={(params) => (
          <TextField
            {...params}
            label="PM"
            placeholder="Select PM"
            sx={{
              width: 190,
              '& .MuiInputBase-root': {
                fontSize: '13px',
                bgcolor: '#f8fafc',
                borderRadius: '6px',
              },
              '& .MuiInputLabel-root': {
                fontSize: '13px',
              },
            }}
          />
        )}
      />

      {/* Search Input Box */}
      <TextField
        size="small"
        placeholder="Search packages..."
        value={filterSearchText}
        onChange={(e) => setFilterSearchText(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={{ color: '#94a3b8', fontSize: 18 }} />
            </InputAdornment>
          ),
          endAdornment: filterSearchText ? (
            <InputAdornment position="end">
              <IconButton
                size="small"
                onClick={() => setFilterSearchText('')}
                edge="end"
                sx={{ p: 0.25 }}
              >
                <ClearIcon sx={{ fontSize: 16, color: '#94a3b8' }} />
              </IconButton>
            </InputAdornment>
          ) : null,
        }}
        sx={{
          width: 220,
          '& .MuiOutlinedInput-root': {
            fontSize: '13px',
            bgcolor: '#f8fafc',
            borderRadius: '6px',
            '& fieldset': {
              borderColor: '#e2e8f0',
            },
            '&:hover fieldset': {
              borderColor: '#cbd5e1',
            },
          },
        }}
      />

      {/* Clear Filters Button */}
      {isAnyFilterActive && (
        <Button
          size="small"
          onClick={onClearFilters}
          sx={{
            textTransform: 'none',
            fontSize: '12px',
            fontWeight: 600,
            color: '#64748b',
            '&:hover': {
              bgcolor: '#f1f5f9',
              color: '#475569',
            },
          }}
          startIcon={<ClearIcon sx={{ fontSize: 14 }} />}
        >
          Clear Filters
        </Button>
      )}
    </Box>
  );
};

export default ProductionMetricsFilterBar;
